// components/video/ImageViewer.tsx - COM VISUALIZAÇÃO DE PLACAS
import DisplacementOverlay from 'components/video/DisplacementOverlay';
import ZoomControls from 'components/video/ZoomControls';
import FeatherIcon from 'feather-icons-react';
import { useImageZoom } from 'hooks/useImageZoom';

import { getServiceColor } from 'constants/serviceColors'; // ✅ SISTEMA DE CORES
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Spinner } from 'react-bootstrap';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:3300/api/v1';

interface ImageViewerProps {
  projectId: number;
  images: any[];
  currentImageIndex: number;
  isPlaying: boolean;
  playbackSpeed: number;
  visibleSigns: any[];
  savedSigns: any[];
  selectedSignIndex?: number | null;
  isBlinking?: boolean;
  onMarkerClick?: (index: number) => void;
  clickedCoordinates: { x: number; y: number } | null;
  showMarker: boolean;
  videoClickEnabled: boolean;
  dragModeEnabled: boolean;
  gpsDisplacementEnabled: boolean;
  gpsDisplacement: any;
  cameraBearing?: number | null;
  onImageChange: (indexOrCallback: number | ((prev: number) => number)) => void;
  onPause: () => void;
  onImageClick: (e: React.MouseEvent<HTMLImageElement>, gpsResult: any) => void;
  onMarkerMouseDown: (e: React.MouseEvent, index: number, sign: any) => void;
  onMarkerMouseMove: (e: React.MouseEvent) => void;
  onMarkerMouseUp: () => void;
  onEditSign: (index: number) => void;
  showSignImages?: boolean; // ✅ NOVO
}

export interface ImageViewerRef {
  navigateFrames: (count: number) => void;
  goToFrame: (frame: number) => void;
  getCurrentFrame: () => number;
  getImageElement: () => HTMLImageElement | null;
}

const ImageViewer = forwardRef<ImageViewerRef, ImageViewerProps>(
  (
    {
      projectId,
      images,
      currentImageIndex,
      isPlaying,
      playbackSpeed,
      visibleSigns,
      savedSigns,
      selectedSignIndex,
      isBlinking,
      onMarkerClick,
      clickedCoordinates,
      showMarker,
      videoClickEnabled,
      dragModeEnabled,
      gpsDisplacementEnabled,
      gpsDisplacement,
      cameraBearing,
      onImageChange,
      onPause,
      onImageClick,
      onMarkerMouseDown,
      onMarkerMouseMove,
      onMarkerMouseUp,
      onEditSign,
      showSignImages = false // ✅ NOVO
    },
    ref
  ) => {
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [imageRect, setImageRect] = useState<DOMRect | null>(null);
    const [imageSrc, setImageSrc] = useState<string>('');
    const imgRef = useRef<HTMLImageElement>(null);
    const playIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const currentBlobUrlRef = useRef<string | null>(null);

    // Cache de imagens
    const imageCacheRef = useRef<Map<number, string>>(new Map());
    const preloadingRef = useRef<Set<number>>(new Set());
    const previousIndexRef = useRef<number>(-1);

    // Configurações de buffer
    const BUFFER_AHEAD = 7;
    const BUFFER_BEHIND = 2;
    const CACHE_CLEANUP_DISTANCE = 10;

    const currentImage = images[currentImageIndex];
    const {
      zoom,
      pan,
      isDragging,
      containerRef,
      zoomIn,
      zoomOut,
      resetZoom,
      handleMouseDown,
      handleMouseMove: handleZoomMouseMove,
      handleMouseUp
    } = useImageZoom({
      minZoom: 1,
      maxZoom: 5,
      zoomStep: 0.25
    });

    // ✅ NOVO: Função para obter caminho da imagem da placa
    const getSignImagePath = (device: any) => {
      if (!device || !device.symbol_name) return '';
      return `/images/signs/${device.symbol_name}`;
    };

    // ✅ NOVO: Função para obter nome do serviço
    const getServiceName = (sign: any) => {
      // Tentar pegar do objeto signal_service primeiro
      if (sign.signal_service && sign.signal_service.name) {
        return sign.signal_service.name;
      }

      // Senão, converter do service_id
      if (sign.service_id) {
        switch (sign.service_id) {
          case 1:
            return 'Implantação';
          case 2:
            return 'Remoção';
          case 3:
            return 'Limpeza';
          case 4:
            return 'Manter';
          default:
            return 'N/A';
        }
      }

      return 'N/A';
    };

    useImperativeHandle(ref, () => ({
      navigateFrames: (count: number) => {
        const newIndex = Math.max(
          0,
          Math.min(currentImageIndex + count, images.length - 1)
        );
        console.log(`[ImageViewer] 🎬 Navegando ${count} frames:`, {
          de: currentImageIndex,
          para: newIndex
        });
        onImageChange(newIndex);
      },
      goToFrame: (frame: number) => {
        const newIndex = Math.max(0, Math.min(frame, images.length - 1));
        console.log(`[ImageViewer] 🎯 Indo para frame ${frame}:`, {
          atual: currentImageIndex,
          novo: newIndex
        });
        onImageChange(newIndex);
      },
      getCurrentFrame: () => currentImageIndex,
      getImageElement: () => imgRef.current
    }));

    useEffect(() => {
      if (isPlaying) {
        console.log(
          `[ImageViewer] ▶️ Play iniciado (velocidade: ${playbackSpeed}x)`
        );

        const interval = 1000 / playbackSpeed;

        playIntervalRef.current = setInterval(() => {
          onImageChange((prev: number) => {
            if (prev >= images.length - 1) {
              console.log('[ImageViewer] 🏁 Fim do slideshow');
              onPause();
              return prev;
            }
            return prev + 1;
          });
        }, interval);
      } else {
        console.log('[ImageViewer] ⏸️ Play pausado');
        if (playIntervalRef.current) {
          clearInterval(playIntervalRef.current);
          playIntervalRef.current = null;
        }
      }

      return () => {
        if (playIntervalRef.current) {
          clearInterval(playIntervalRef.current);
        }
      };
    }, [isPlaying, playbackSpeed, images.length, onImageChange, onPause]);

    const loadImageToCache = useCallback(
      async (imageIndex: number) => {
        if (imageIndex < 0 || imageIndex >= images.length) return;

        if (imageCacheRef.current.has(imageIndex)) {
          console.log(`[Cache] ✅ Hit: ${imageIndex} (já em cache)`);
          return;
        }

        if (preloadingRef.current.has(imageIndex)) {
          console.log(`[Cache] ⏳ Já carregando: ${imageIndex}`);
          return;
        }

        const image = images[imageIndex];
        if (!image) return;

        preloadingRef.current.add(imageIndex);
        console.log(`[Cache] 🔄 Carregando: ${imageIndex}`);

        try {
          let imageData: string;

          if (image.filedata) {
            const mimeType = image.filetype || 'image/jpeg';
            imageData = `data:${mimeType};base64,${image.filedata}`;
          } else {
            const baseUrl = API_BASE_URL.replace(/\/$/, '');
            const imageUrl = `${baseUrl}/projects/${projectId}/image/${image.id}`;
            const token = localStorage.getItem('authToken');

            const response = await fetch(imageUrl, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
              const data = await response.json();
              if (data.filedata) {
                const mimeType =
                  data.mime_type || data.filetype || 'image/jpeg';
                imageData = `data:${mimeType};base64,${data.filedata}`;
              } else {
                throw new Error('JSON sem filedata');
              }
            } else {
              const blob = await response.blob();
              imageData = URL.createObjectURL(blob);
            }
          }

          imageCacheRef.current.set(imageIndex, imageData);
          console.log(
            `[Cache] ✅ Adicionado: ${imageIndex} (total em cache: ${imageCacheRef.current.size})`
          );
        } catch (error) {
          console.error(`[Cache] ❌ Erro ao carregar ${imageIndex}:`, error);
        } finally {
          preloadingRef.current.delete(imageIndex);
        }
      },
      [images, projectId]
    );

    useEffect(() => {
      const preloadSmartBuffer = async () => {
        const startIndex = Math.max(0, currentImageIndex - BUFFER_BEHIND);
        const endIndex = Math.min(
          images.length - 1,
          currentImageIndex + BUFFER_AHEAD
        );

        const movingForward = currentImageIndex > previousIndexRef.current;
        previousIndexRef.current = currentImageIndex;

        console.log(`[Buffer] 📊 Frame atual: ${currentImageIndex}`);
        console.log(
          `[Buffer] 📊 Range necessário: ${startIndex} até ${endIndex}`
        );
        console.log(
          `[Buffer] 📊 Cache atual tem ${imageCacheRef.current.size} imagens`
        );

        const promises: Promise<void>[] = [];
        const toLoad: number[] = [];

        if (
          !imageCacheRef.current.has(currentImageIndex) &&
          !preloadingRef.current.has(currentImageIndex)
        ) {
          toLoad.push(currentImageIndex);
        }

        if (movingForward) {
          for (let i = currentImageIndex + 1; i <= endIndex; i++) {
            if (
              !imageCacheRef.current.has(i) &&
              !preloadingRef.current.has(i)
            ) {
              toLoad.push(i);
            }
          }
          for (let i = currentImageIndex - 1; i >= startIndex; i--) {
            if (
              !imageCacheRef.current.has(i) &&
              !preloadingRef.current.has(i)
            ) {
              toLoad.push(i);
            }
          }
        } else {
          for (let i = currentImageIndex - 1; i >= startIndex; i--) {
            if (
              !imageCacheRef.current.has(i) &&
              !preloadingRef.current.has(i)
            ) {
              toLoad.push(i);
            }
          }
          for (let i = currentImageIndex + 1; i <= endIndex; i++) {
            if (
              !imageCacheRef.current.has(i) &&
              !preloadingRef.current.has(i)
            ) {
              toLoad.push(i);
            }
          }
        }

        console.log(
          `[Buffer] 🆕 Novas imagens para carregar: ${toLoad.length}`,
          toLoad
        );

        toLoad.forEach(index => {
          promises.push(loadImageToCache(index));
        });

        const keysToDelete: number[] = [];
        imageCacheRef.current.forEach((src, index) => {
          if (
            index < startIndex - CACHE_CLEANUP_DISTANCE ||
            index > endIndex + CACHE_CLEANUP_DISTANCE
          ) {
            keysToDelete.push(index);
            if (src.startsWith('blob:')) {
              URL.revokeObjectURL(src);
            }
          }
        });

        if (keysToDelete.length > 0) {
          console.log(
            `[Cache] 🧹 Limpando ${keysToDelete.length} imagens distantes:`,
            keysToDelete
          );
          keysToDelete.forEach(key => {
            imageCacheRef.current.delete(key);
          });
        }

        await Promise.all(promises);
        console.log(
          `[Buffer] ✅ Buffer atualizado. Cache total: ${imageCacheRef.current.size} imagens`
        );
      };

      preloadSmartBuffer();
    }, [currentImageIndex, loadImageToCache, images.length]);

    useEffect(() => {
      if (!currentImage) return;

      let cancelled = false;
      let retryCount = 0;
      const MAX_RETRIES = 5;
      const RETRY_DELAY = 200;

      const loadCurrentImage = async () => {
        setImageLoading(true);
        setImageError(false);

        const tryGetFromCache = async (): Promise<string | null> => {
          if (imageCacheRef.current.has(currentImageIndex)) {
            return imageCacheRef.current.get(currentImageIndex)!;
          }

          if (
            preloadingRef.current.has(currentImageIndex) &&
            retryCount < MAX_RETRIES
          ) {
            retryCount++;
            console.log(
              `[ImageViewer] ⏳ Aguardando frame ${currentImageIndex} (tentativa ${retryCount}/${MAX_RETRIES})`
            );
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            return tryGetFromCache();
          }

          return null;
        };

        try {
          let cachedSrc = await tryGetFromCache();

          if (cachedSrc && !cancelled) {
            console.log(
              `[ImageViewer] 📦 Usando cache para frame ${currentImageIndex}`
            );
            setImageSrc(cachedSrc);
            setImageLoading(false);
            setImageError(false);
            return;
          }

          console.log(
            `[ImageViewer] 🔄 Cache miss, carregando frame ${currentImageIndex}`
          );

          await loadImageToCache(currentImageIndex);

          cachedSrc = await tryGetFromCache();

          if (!cancelled && cachedSrc) {
            console.log(
              `[ImageViewer] ✅ Frame ${currentImageIndex} carregado e pronto para exibir`
            );
            setImageSrc(cachedSrc);
            setImageLoading(false);
            setImageError(false);
          } else if (!cancelled) {
            console.error(
              `[ImageViewer] ❌ Frame ${currentImageIndex} não pôde ser carregado após ${MAX_RETRIES} tentativas`
            );
            setImageError(true);
            setImageLoading(false);
          }
        } catch (error) {
          if (!cancelled) {
            console.error(
              '[ImageViewer] ❌ Erro ao carregar frame atual:',
              error
            );
            setImageError(true);
            setImageLoading(false);
          }
        }
      };

      loadCurrentImage();

      return () => {
        cancelled = true;
      };
    }, [currentImage, currentImageIndex, projectId, loadImageToCache]);

    useEffect(() => {
      return () => {
        if (currentBlobUrlRef.current) {
          URL.revokeObjectURL(currentBlobUrlRef.current);
          currentBlobUrlRef.current = null;
        }

        console.log(
          `[Cache] 🗑️ Limpando ${imageCacheRef.current.size} imagens do cache`
        );
        imageCacheRef.current.forEach(src => {
          if (src.startsWith('blob:')) {
            URL.revokeObjectURL(src);
          }
        });
        imageCacheRef.current.clear();
      };
    }, []);

    useEffect(() => {
      const handleZoomIn = () => zoomIn();
      const handleZoomOut = () => zoomOut();
      const handleResetZoom = () => resetZoom();

      window.addEventListener('zoomIn', handleZoomIn);
      window.addEventListener('zoomOut', handleZoomOut);
      window.addEventListener('resetZoom', handleResetZoom);

      return () => {
        window.removeEventListener('zoomIn', handleZoomIn);
        window.removeEventListener('zoomOut', handleZoomOut);
        window.removeEventListener('resetZoom', handleResetZoom);
      };
    }, [zoomIn, zoomOut, resetZoom]);

    useEffect(() => {
      const updateRect = () => {
        if (imgRef.current) {
          setImageRect(imgRef.current.getBoundingClientRect());
        }
      };

      const timer = setTimeout(updateRect, 100);
      window.addEventListener('resize', updateRect);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateRect);
      };
    }, [currentImageIndex, imageSrc]);

    const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
      console.log('[ImageViewer] 🖱️ Click detectado:', {
        videoClickEnabled,
        gpsDisplacementEnabled,
        currentImageIndex
      });

      if (!videoClickEnabled) {
        console.log('[ImageViewer] ❌ Clique desabilitado');
        return;
      }

      const gpsResult = gpsDisplacementEnabled
        ? gpsDisplacement.displacementResult
        : null;

      console.log('[ImageViewer] ✅ Processando clique');

      const clickEvent = new CustomEvent('imageViewerClick', {
        detail: {
          event: e,
          currentImageIndex,
          gpsResult
        }
      });

      window.dispatchEvent(clickEvent);
      onImageClick(e, gpsResult);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (zoom > 1) {
        handleZoomMouseMove(e);
      }

      if (gpsDisplacementEnabled && imgRef.current) {
        const imgRect = imgRef.current.getBoundingClientRect();
        const relativeX = (e.clientX - imgRect.left) / imgRect.width;
        const relativeY = (e.clientY - imgRect.top) / imgRect.height;

        window.dispatchEvent(
          new CustomEvent('gpsMouseMove', {
            detail: {
              relativeX,
              relativeY,
              imageRect: {
                width: imgRect.width,
                height: imgRect.height,
                left: imgRect.left,
                top: imgRect.top
              }
            }
          })
        );
      }

      if (onMarkerMouseMove) {
        onMarkerMouseMove(e);
      }
    };

    const handleMouseLeave = () => {
      if (gpsDisplacementEnabled) {
        window.dispatchEvent(new CustomEvent('gpsMouseLeave'));
      }
      onMarkerMouseUp();
    };

    if (!currentImage) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#000'
          }}
        >
          <p className="text-light">Nenhuma imagem disponível</p>
        </div>
      );
    }

    return (
      <div
        className="position-relative h-100 d-flex flex-column"
        style={{ backgroundColor: '#000' }}
      >
        <style>{`
          @keyframes pulse-marker {
            0%, 100% { 
              transform: translate(-50%, -50%) scale(1); 
              box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            }
            50% { 
              transform: translate(-50%, -50%) scale(1.2); 
              box-shadow: 0 4px 12px rgba(0,0,0,0.5);
            }
          }
          
          @keyframes pulse-sign-badge-viewer {
            0%, 100% { 
              transform: translate(-50%, -50%) scale(1); 
              opacity: 0.5;
            }
            50% { 
              transform: translate(-50%, -50%) scale(1.3); 
              opacity: 0.2;
            }
          }
          
          .selected-marker {
            animation: pulse-marker 0.5s ease-in-out 4;
            z-index: 10 !important;
          }
        `}</style>

        <div
          className="position-relative flex-grow-1"
          onMouseMove={handleMouseMove}
          onMouseUp={onMarkerMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {imageSrc && (
            <div
              ref={containerRef}
              style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                cursor:
                  zoom > 1
                    ? isDragging
                      ? 'grabbing'
                      : 'grab'
                    : gpsDisplacementEnabled
                    ? 'crosshair'
                    : videoClickEnabled
                    ? 'crosshair'
                    : dragModeEnabled
                    ? 'grab'
                    : 'default'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt={`Frame ${currentImageIndex}`}
                onLoad={() => {
                  console.log(
                    `[ImageViewer] ✅ Renderizada: ${currentImageIndex}`
                  );
                  setImageLoading(false);
                  setImageError(false);
                }}
                onError={e => {
                  console.error(
                    `[ImageViewer] ❌ Erro render: ${currentImageIndex}`,
                    e
                  );
                  setImageLoading(false);
                  setImageError(true);
                }}
                onClick={handleImageClick}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#000',
                  transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${
                    pan.y / zoom
                  }px)`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                  userSelect: 'none',
                  pointerEvents: zoom > 1 ? 'none' : 'auto',
                  cursor:
                    zoom > 1
                      ? 'grab'
                      : gpsDisplacementEnabled
                      ? 'crosshair'
                      : videoClickEnabled
                      ? 'crosshair'
                      : dragModeEnabled
                      ? 'grab'
                      : 'default',
                  display: imageError ? 'none' : 'block',
                  opacity: imageLoading ? 0.5 : 1
                }}
                draggable={false}
              />

              <ZoomControls
                zoom={zoom}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onResetZoom={resetZoom}
              />
            </div>
          )}

          {imageLoading && (
            <div
              className="position-absolute top-50 start-50 translate-middle text-center"
              style={{ zIndex: 2 }}
            >
              <Spinner animation="border" variant="light" />
              <div className="text-light mt-2">
                <small>Carregando frame {currentImageIndex}...</small>
              </div>
            </div>
          )}

          {imageError && (
            <div
              className="position-absolute top-50 start-50 translate-middle text-center"
              style={{ zIndex: 2 }}
            >
              <div className="alert alert-danger">
                <FeatherIcon icon="alert-circle" size={24} className="mb-2" />
                <div>Erro ao carregar imagem</div>
                <small>Frame: {currentImageIndex}</small>
              </div>
            </div>
          )}

          {gpsDisplacementEnabled && gpsDisplacement && (
            <DisplacementOverlay
              mousePosition={gpsDisplacement.mousePosition}
              videoDisplacement={gpsDisplacement.videoDisplacement}
              displacementResult={gpsDisplacement.displacementResult}
              videoRect={imageRect}
              show={gpsDisplacement.isActive}
              cameraBearing={cameraBearing}
            />
          )}

          {showMarker && clickedCoordinates && (
            <div
              className="position-absolute"
              style={{
                left: `${clickedCoordinates.x * 100}%`,
                top: `${clickedCoordinates.y * 100}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
                pointerEvents: 'none'
              }}
            >
              <div
                className="bg-danger rounded-circle border border-light"
                style={{
                  width: '12px',
                  height: '12px',
                  boxShadow: '0 0 10px rgba(220, 53, 69, 0.8)'
                }}
              />
            </div>
          )}

          {/* ✅ NOVO: Markers com visualização condicional de placas */}
          {visibleSigns &&
            Array.isArray(visibleSigns) &&
            visibleSigns.length > 0 &&
            visibleSigns.map((sign, visibleIndex) => {
              const actualIndex = savedSigns.findIndex(s => s.id === sign.id);
              const isSelected = selectedSignIndex === actualIndex;
              const signImagePath = getSignImagePath(sign.signal_device);

              // Debug: verificar estrutura COMPLETA do objeto sign
              if (visibleIndex === 0) {
                console.log('[ImageViewer] 🔍 OBJETO SIGN COMPLETO:', sign);
                console.log(
                  '[ImageViewer] 🔍 CHAVES do sign:',
                  Object.keys(sign)
                );
                console.log('[ImageViewer] 🔍 service_id:', sign.service_id);
                console.log(
                  '[ImageViewer] 🔍 signal_service:',
                  sign.signal_service
                );
                console.log(
                  '[ImageViewer] 🔍 Nome do Serviço:',
                  getServiceName(sign)
                );
                console.log(
                  '[ImageViewer] 🔍 signal_device:',
                  sign.signal_device
                );
                if (sign.signal_device) {
                  console.log(
                    '[ImageViewer] 🔍 CHAVES do signal_device:',
                    Object.keys(sign.signal_device)
                  );
                }
              }

              return (
                <div
                  key={`sign-marker-${sign.id}-${visibleIndex}`}
                  className={`position-absolute ${
                    isSelected ? 'selected-marker' : ''
                  }`}
                  style={{
                    left: `${sign.x * 100}%`,
                    top: `${sign.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: isSelected ? 6 : 3,
                    pointerEvents: 'auto',
                    cursor: dragModeEnabled ? 'grab' : 'pointer',
                    transition: 'all 0.2s',
                    animation:
                      isSelected && isBlinking
                        ? 'pulse-marker 0.5s ease-in-out 4'
                        : 'none'
                  }}
                  onMouseDown={e => onMarkerMouseDown(e, actualIndex, sign)}
                  onClick={e => {
                    if (!dragModeEnabled) {
                      e.stopPropagation();
                      console.log(
                        `[ImageViewer] 🎯 Marker ${actualIndex + 1} clicado`
                      );
                      if (onMarkerClick) {
                        onMarkerClick(actualIndex);
                      }
                    }
                  }}
                  onDoubleClick={e => {
                    if (!dragModeEnabled) {
                      e.stopPropagation();
                      console.log(
                        `[ImageViewer] ✏️ Editando ${actualIndex + 1}`
                      );
                      onEditSign(actualIndex);
                    }
                  }}
                >
                  {/* ✅ NOVO: Renderização condicional - Imagem ou Círculo */}
                  {showSignImages && signImagePath ? (
                    // Modo Imagem da Placa
                    <>
                      {/* Label com código e tipo de serviço */}
                      <div
                        className="position-absolute border rounded px-1"
                        style={{
                          top: isSelected ? '-32px' : '-25px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: isSelected ? '12px' : '10px',
                          whiteSpace: 'nowrap',
                          backgroundColor: isSelected ? '#0d6efd' : 'white',
                          color: isSelected ? 'white' : 'black',
                          fontWeight: isSelected ? 'bold' : 'normal',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          zIndex: 10
                        }}
                      >
                        {sign.signal_device?.code || `S${actualIndex + 1}`} -{' '}
                        {getServiceName(sign)}
                      </div>

                      <div
                        style={{
                          position: 'relative',
                          filter: isSelected
                            ? 'drop-shadow(0 0 6px rgba(13, 110, 253, 0.9)) drop-shadow(0 2px 4px rgba(0,0,0,0.4))'
                            : 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))'
                        }}
                      >
                        <img
                          src={signImagePath}
                          alt={`Placa ${actualIndex + 1}`}
                          style={{
                            width: isSelected ? '40px' : '36px',
                            height: isSelected ? '40px' : '36px',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                            transition: 'all 0.3s ease'
                          }}
                          onError={e => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />

                        {/* Badge com número */}
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-6px',
                            right: '-6px',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor:
                              sign.signal_device?.type === 'regulamentacao'
                                ? '#dc3545'
                                : sign.signal_device?.type === 'advertencia'
                                ? '#ffc107'
                                : '#0d6efd',
                            border: isSelected
                              ? '2px solid #0d6efd'
                              : '2px solid white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: isSelected
                              ? '0 0 0 3px rgba(13, 110, 253, 0.3), 0 1px 3px rgba(0,0,0,0.4)'
                              : '0 1px 3px rgba(0,0,0,0.4)'
                          }}
                        >
                          <small
                            className="text-white fw-bold"
                            style={{ fontSize: '9px' }}
                          >
                            {actualIndex + 1}
                          </small>

                          {/* Pulso de seleção no badge */}
                          {isSelected && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                border: '2px solid #0d6efd',
                                opacity: 0.5,
                                animation:
                                  'pulse-sign-badge-viewer 1s ease-in-out infinite',
                                pointerEvents: 'none'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    // Modo Círculo Padrão
                    <>
                      <div
                        className="rounded-circle border d-flex align-items-center justify-content-center"
                        style={{
                          width: isSelected ? '32px' : '24px',
                          height: isSelected ? '32px' : '24px',
                          backgroundColor: getServiceColor(sign.service_id)
                            .rgbaColor, // ✅ COR DO SERVIÇO
                          borderColor: isSelected ? '#000000' : 'white',
                          borderWidth: isSelected ? '3px' : '2px',
                          boxShadow: isSelected
                            ? '0 0 0 4px rgba(13, 110, 253, 0.25), 0 4px 12px rgba(0,0,0,0.5)'
                            : '0 2px 6px rgba(0,0,0,0.3)'
                        }}
                      >
                        <small
                          className="text-white fw-bold"
                          style={{ fontSize: isSelected ? '14px' : '10px' }}
                        >
                          {actualIndex + 1}
                        </small>
                      </div>

                      <div
                        className="position-absolute border rounded px-1"
                        style={{
                          top: isSelected ? '-32px' : '-25px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: isSelected ? '12px' : '10px',
                          whiteSpace: 'nowrap',
                          backgroundColor: isSelected ? '#0d6efd' : 'white',
                          color: isSelected ? 'white' : 'black',
                          fontWeight: isSelected ? 'bold' : 'normal',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}
                      >
                        {sign.signal_device?.code || `S${actualIndex + 1}`} -{' '}
                        {getServiceName(sign)}
                      </div>
                    </>
                  )}
                </div>
              );
            })}

          <div
            className="position-absolute bottom-0 end-0 m-2 px-2 py-1 rounded"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontSize: '12px',
              fontFamily: 'monospace',
              zIndex: 100
            }}
          >
            <div>
              Frame: {currentImageIndex + 1} / {images.length}
            </div>
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              Cache: {imageCacheRef.current.size} imgs | Loading:{' '}
              {preloadingRef.current.size}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ImageViewer.displayName = 'ImageViewer';

export default ImageViewer;
